import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { ProfileContext } from "../../../context/ProfileProvider";
import "./withdrawal.css";

const EMPTY_METHOD_FLAGS = { email: false, phone: false, authApp: false, passkeys: false };
const EMPTY_PROOF = { ...EMPTY_METHOD_FLAGS };

function remainingFlags(enabled, unavailable) {
  return {
    authApp: !!(enabled.authApp && !unavailable.authApp),
    email: !!(enabled.email && !unavailable.email),
    phone: !!(enabled.phone && !unavailable.phone),
    passkeys: !!(enabled.passkeys && !unavailable.passkeys),
  };
}

function selectedBackendMethodsFrom(unavailable, enabled) {
  const methods = [];
  if (unavailable.email && enabled.email) methods.push("email");
  if (unavailable.phone && enabled.phone) methods.push("mobile");
  if (unavailable.authApp && enabled.authApp) methods.push("totp");
  if (unavailable.passkeys && enabled.passkeys) methods.push("passkey");
  return methods;
}

function flagsFromBackendMethods(methods) {
  const list = Array.isArray(methods) ? methods : [];
  return {
    email: list.includes("email"),
    phone: list.includes("mobile"),
    authApp: list.includes("totp"),
    passkeys: list.includes("passkey"),
  };
}

function unwrapProgress(result) {
  const nested = result?.data?.data;
  if (nested && Array.isArray(nested.required_methods)) return nested;
  if (result?.data && Array.isArray(result.data.required_methods)) return result.data;
  return result?.data || {};
}

function maskEmailForDisplay(raw) {
  if (!raw || typeof raw !== "string") return "you***@email.com";
  const at = raw.indexOf("@");
  if (at <= 0) return "***";
  const local = raw.slice(0, at);
  const domain = raw.slice(at + 1);
  const visible = Math.min(3, local.length);
  return `${local.slice(0, visible)}***@${domain}`;
}

function maskPhoneDigits(digits) {
  const d = String(digits || "").replace(/\D/g, "");
  if (d.length < 4) return "your phone";
  return `***${d.slice(-4)}`;
}

export default function SecurityVerificationUnavailable() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails = {}, refreshSecurityMethods } = useContext(ProfileContext) || {};
  const preselectMethods = location.state?.preselectMethods;
  const preselectKey = Array.isArray(preselectMethods) ? preselectMethods.join(",") : "";
  const preselectAppliedRef = useRef(false);

  const accountEmail = String(userDetails?.emailId || userDetails?.email || "").trim();
  const accountPhoneDigits = String(userDetails?.mobileNumber || userDetails?.phone || "").replace(/\D/g, "");
  const maskedEmail = useMemo(() => maskEmailForDisplay(accountEmail), [accountEmail]);
  const maskedPhone = useMemo(() => maskPhoneDigits(accountPhoneDigits), [accountPhoneDigits]);

  const [step, setStep] = useState("select"); // select | confirm | requirements | success
  const [enabledSecurityMethods, setEnabledSecurityMethods] = useState({
    email: false,
    phone: false,
    authApp: false,
    passkeys: false,
  });

  const [secUnavailable, setSecUnavailable] = useState({
    email: false,
    phone: false,
    authApp: false,
    passkeys: false,
  });
  const [requiredMethods, setRequiredMethods] = useState([]);
  const [resetToken, setResetToken] = useState("");
  const [startBusy, setStartBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const result = await AuthService.getSecurityMethodsList();
        if (cancelled) return;

        const raw =
          result?.data?.security_methods ||
          result?.data?.data?.security_methods ||
          result?.security_methods ||
          result?.data?.securityMethods ||
          {};

        const nextEnabled = {
          email: !!raw.email,
          phone: !!(raw.phone ?? raw.mobile ?? raw.sms),
          authApp: !!raw.totp,
          passkeys: !!raw.passkey,
        };
        setEnabledSecurityMethods(nextEnabled);

        if (!preselectAppliedRef.current && Array.isArray(preselectMethods) && preselectMethods.length) {
          preselectAppliedRef.current = true;
          setSecUnavailable((prev) => ({
            ...prev,
            email: preselectMethods.includes("email") ? nextEnabled.email : prev.email,
            phone: preselectMethods.includes("phone") ? nextEnabled.phone : prev.phone,
            authApp: preselectMethods.includes("authApp") ? nextEnabled.authApp : prev.authApp,
            passkeys: preselectMethods.includes("passkeys") ? nextEnabled.passkeys : prev.passkeys,
          }));
        }
      } catch {
        setEnabledSecurityMethods({ ...EMPTY_METHOD_FLAGS });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [preselectKey]);

  const methodRows = useMemo(() => {
    const rows = [];

    if (enabledSecurityMethods.email) {
      rows.push({
        key: "email",
        title: "Email",
        subtitle: accountEmail ? maskedEmail : "No email on file",
        icon: "/images/email_vector2.svg",
      });
    }
    if (enabledSecurityMethods.phone) {
      rows.push({ key: "phone", title: "Phone Number", subtitle: accountPhoneDigits ? maskedPhone : "No phone on file", icon: "/images/security/phone.svg" });
    }
    if (enabledSecurityMethods.authApp) {
      rows.push({ key: "authApp", title: "Authenticator App", subtitle: "", icon: "/images/security/authenticator.svg" });
    }
    if (enabledSecurityMethods.passkeys) {
      rows.push({ key: "passkeys", title: "Passkeys", subtitle: "", icon: "/images/security/passkey.svg" });
    }

    return rows;
  }, [accountEmail, accountPhoneDigits, enabledSecurityMethods, maskedEmail, maskedPhone]);

  const selectedCount = useMemo(
    () =>
      Object.keys(EMPTY_METHOD_FLAGS).filter(
        (key) => secUnavailable[key] && enabledSecurityMethods[key]
      ).length,
    [enabledSecurityMethods, secUnavailable]
  );

  const remainingVerify = useMemo(() => {
    if (Array.isArray(requiredMethods) && requiredMethods.length) {
      return flagsFromBackendMethods(requiredMethods);
    }
    return remainingFlags(enabledSecurityMethods, secUnavailable);
  }, [enabledSecurityMethods, requiredMethods, secUnavailable]);

  const [secResetAck, setSecResetAck] = useState(true);
  const [resetBusy, setResetBusy] = useState(false);

  const [secReqDone, setSecReqDone] = useState({
    authApp: false,
    email: false,
    phone: false,
    passkeys: false,
  });

  const remainingProofDone = useMemo(
    () =>
      !!resetToken &&
      (!remainingVerify.authApp || secReqDone.authApp) &&
      (!remainingVerify.email || secReqDone.email) &&
      (!remainingVerify.phone || secReqDone.phone) &&
      (!remainingVerify.passkeys || secReqDone.passkeys),
    [remainingVerify, resetToken, secReqDone]
  );

  const reqTotal = useMemo(() => {
    return (
      Number(remainingVerify.authApp) +
      Number(remainingVerify.email) +
      Number(remainingVerify.phone) +
      Number(remainingVerify.passkeys)
    );
  }, [remainingVerify]);

  const reqCompleted = useMemo(() => {
    return (
      Number(remainingVerify.authApp && secReqDone.authApp) +
      Number(remainingVerify.email && secReqDone.email) +
      Number(remainingVerify.phone && secReqDone.phone) +
      Number(remainingVerify.passkeys && secReqDone.passkeys)
    );
  }, [remainingVerify, secReqDone]);

  // Verification modals
  const [isPasskeyOpen, setIsPasskeyOpen] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [isAuthAppOpen, setIsAuthAppOpen] = useState(false);
  const [authAppCode, setAuthAppCode] = useState("");
  const [isEmailVerifyOpen, setIsEmailVerifyOpen] = useState(false);
  const [isMobileVerifyOpen, setIsMobileVerifyOpen] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [emailOtp, setEmailOtp] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [isRequestSubmittedOpen, setIsRequestSubmittedOpen] = useState(false);
  const [isSecurityResetSuccessOpen, setIsSecurityResetSuccessOpen] = useState(false);

  const closeAll = () => {
    setIsPasskeyOpen(false);
    setIsAuthAppOpen(false);
    setIsEmailVerifyOpen(false);
    setIsMobileVerifyOpen(false);
    setEmailOtp("");
    setMobileOtp("");
    setIsRequestSubmittedOpen(false);
    setIsSecurityResetSuccessOpen(false);
  };

  // Prevent background scroll/click bubbling while the modal page is open.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, []);

  const openProof = useCallback((kind) => {
    closeAll();
    if (kind === "passkeys") setIsPasskeyOpen(true);
    else if (kind === "authApp") setIsAuthAppOpen(true);
    else if (kind === "phone") setIsMobileVerifyOpen(true);
    else if (kind === "email") setIsEmailVerifyOpen(true);
  }, []);

  const proofOverlayOpen =
    isPasskeyOpen ||
    isAuthAppOpen ||
    isEmailVerifyOpen ||
    isMobileVerifyOpen ||
    isRequestSubmittedOpen;

  const selectedBackendMethods = useCallback(
    () => selectedBackendMethodsFrom(secUnavailable, enabledSecurityMethods),
    [enabledSecurityMethods, secUnavailable]
  );

  const applyProgress = useCallback((result) => {
    const progress = unwrapProgress(result);
    const required = Array.isArray(progress.required_methods) ? progress.required_methods : [];
    const verified = Array.isArray(progress.verified_methods) ? progress.verified_methods : [];
    setRequiredMethods(required);
    setSecReqDone(flagsFromBackendMethods(verified));
    if (progress.resetToken) {
      setResetToken(String(progress.resetToken));
    } else if (progress.complete !== true) {
      setResetToken("");
    }
    return progress;
  }, []);

  const markPasskeyUnavailable = useCallback(() => {
    setIsPasskeyOpen(false);
    setSecUnavailable((prev) => ({ ...prev, passkeys: true }));
    setSecReqDone({ ...EMPTY_PROOF });
    setRequiredMethods([]);
    setResetToken("");
    setStep("select");
  }, []);

  const verifyPasskeyProof = useCallback(async () => {
    if (passkeyBusy) return;
    setPasskeyBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      const optionsResult = await AuthService.passkeyGetStepUpOptions();
      if (!optionsResult?.success || !optionsResult?.data) {
        alertErrorMessage(optionsResult?.message || "Failed to start passkey verification");
        return;
      }
      LoaderHelper.loaderStatus(false);
      let credential;
      try {
        credential = await startAuthentication(optionsResult.data);
      } catch (webauthnError) {
        if (webauthnError?.name === "NotAllowedError") {
          alertErrorMessage("Authentication was cancelled or timed out.");
          return;
        }
        alertErrorMessage("Passkey verification failed.");
        return;
      }
      LoaderHelper.loaderStatus(true);
      const verifyResult = await AuthService.verifyAllSecurityMethods({
        purpose: "unavailable_reset",
        type: "passkey",
        credential,
      });
      if (!verifyResult?.success) {
        alertErrorMessage(verifyResult?.message || "Passkey verification failed");
        return;
      }
      applyProgress(verifyResult);
      setIsPasskeyOpen(false);
      alertSuccessMessage("Passkey verified");
    } catch (error) {
      alertErrorMessage(error?.message || "Passkey verification failed");
    } finally {
      setPasskeyBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  }, [applyProgress, passkeyBusy]);

  const startUnavailableResetSession = useCallback(async () => {
    const methods = selectedBackendMethods();
    if (!methods.length) {
      alertErrorMessage("Select at least one unavailable security method.");
      return false;
    }
    if (!secResetAck) {
      alertErrorMessage("Confirm the security freeze before continuing.");
      return false;
    }
    setStartBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.startUnavailableSecurityReset({
        methods,
        freeze_acknowledged: true,
      });
      if (!result?.success) {
        alertErrorMessage(result?.message || "Failed to start security verification.");
        return false;
      }
      setResetToken("");
      setSecReqDone({ ...EMPTY_PROOF });
      applyProgress(result);
      setStep("requirements");
      return true;
    } catch (error) {
      alertErrorMessage(error?.message || "Failed to start security verification.");
      return false;
    } finally {
      setStartBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  }, [applyProgress, secResetAck, selectedBackendMethods]);

  const submitUnavailableReset = useCallback(async () => {
    const methods = selectedBackendMethods();
    if (!methods.length) {
      alertErrorMessage("Select at least one unavailable security method.");
      return false;
    }
    if (!secResetAck) {
      alertErrorMessage("Confirm the security freeze before continuing.");
      return false;
    }
    if (!resetToken) {
      alertErrorMessage("Verify all remaining security methods before continuing.");
      return false;
    }
    setResetBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.resetUnavailableSecurityMethods({
        methods,
        freeze_acknowledged: true,
        resetToken,
      });
      if (!result?.success) {
        alertErrorMessage(result?.message || "Failed to reset security methods.");
        return false;
      }
      try {
        await refreshSecurityMethods?.();
      } catch {
        /* cache refresh is optional */
      }
      alertSuccessMessage(result?.message || "Selected security methods were disabled.");
      setIsPasskeyOpen(false);
      setIsAuthAppOpen(false);
      setIsEmailVerifyOpen(false);
      setIsMobileVerifyOpen(false);
      setIsRequestSubmittedOpen(false);
      setResetToken("");
      setStep("success");
      return true;
    } catch (error) {
      alertErrorMessage(error?.message || "Failed to reset security methods.");
      return false;
    } finally {
      setResetBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  }, [refreshSecurityMethods, resetToken, secResetAck, selectedBackendMethods]);

  const verifyTotpCode = useCallback(async () => {
    const code = String(authAppCode || "").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      alertErrorMessage("Enter the 6-digit authenticator code.");
      return false;
    }
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.verifyAllSecurityMethods({
        purpose: "unavailable_reset",
        type: "totp",
        code,
      });
      if (!result?.success) {
        alertErrorMessage(result?.message || "Invalid authenticator code");
        return false;
      }
      applyProgress(result);
      setIsAuthAppOpen(false);
      setAuthAppCode("");
      return true;
    } catch (error) {
      alertErrorMessage(error?.message || "Verification failed");
      return false;
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  }, [applyProgress, authAppCode]);

  const goSecurityHome = useCallback(() => {
    navigate("/user_profile/security", { replace: true });
  }, [navigate]);

  const sendChannelOtp = useCallback(async (channel) => {
    const security_methods = channel === "mobile" ? "mobile" : "email";
    setOtpSending(true);
    try {
      const result = await AuthService.sendOtpForEmailOrMobile(security_methods);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code sent");
        return true;
      }
      alertErrorMessage(result?.message || "Failed to send verification code");
      return false;
    } catch (error) {
      alertErrorMessage(error?.message || "Failed to send verification code");
      return false;
    } finally {
      setOtpSending(false);
    }
  }, []);

  const verifyChannelOtp = useCallback(async (channel) => {
    const type = channel === "mobile" ? "mobile" : "email";
    const code = String(channel === "mobile" ? mobileOtp : emailOtp).replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      alertErrorMessage("Enter the 6-digit verification code.");
      return false;
    }
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.verifyAllSecurityMethods({
        purpose: "unavailable_reset",
        type,
        code,
      });
      if (!result?.success) {
        alertErrorMessage(result?.message || "Invalid OTP");
        return false;
      }
      applyProgress(result);
      if (type === "mobile") {
        setIsMobileVerifyOpen(false);
        setMobileOtp("");
      } else {
        setIsEmailVerifyOpen(false);
        setEmailOtp("");
      }
      return true;
    } catch (error) {
      alertErrorMessage(error?.message || "Verification failed");
      return false;
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  }, [applyProgress, emailOtp, mobileOtp]);

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap kycemail-phone_popup">
          <main className="so-page" aria-label="Security verification unavailable">
            {/* Step: Select */}
            {step === "select" ? (
              <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
                <div className="withdraw_sec_modal" role="dialog" aria-modal="true" aria-label="Select Unavailable Methods">
                  <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={() => navigate(-1)}>
                    ×
                  </button>

                  <div className="withdraw_sec_title">Select Unavailable Methods</div>
                  <div className="withdraw_sec_subtitle">
                    Please select all security methods you can no longer access and want to reset.
                  </div>

                  <div className="withdraw_sec_list">
                    {methodRows.length === 0 ? (
                      <div className="withdraw_sec_item" style={{ cursor: "default", opacity: 0.7 }}>
                        <span className="withdraw_sec_item_left">
                          <span className="withdraw_sec_item_txt">
                            <span className="withdraw_sec_item_title">No active security methods found</span>
                          </span>
                        </span>
                      </div>
                    ) : null}

                    {methodRows.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        className="withdraw_sec_item"
                        onClick={() => setSecUnavailable((p) => ({ ...p, [item.key]: !p[item.key] }))}
                      >
                        <span className="withdraw_sec_item_left">
                          <span className="withdraw_sec_icon" aria-hidden="true">
                            <img src={item.icon} alt="" />
                          </span>
                          <span className="withdraw_sec_item_txt">
                            <span className="withdraw_sec_item_title">{item.title}</span>
                            {item.subtitle ? <span className="withdraw_sec_item_sub">{item.subtitle}</span> : null}
                          </span>
                        </span>
                        <span className={`withdraw_sec_check${secUnavailable[item.key] ? " is-checked" : ""}`} aria-hidden="true" />
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="withdraw_sec_primary"
                    disabled={selectedCount === 0 || methodRows.length === 0}
                    onClick={() => setStep("confirm")}
                  >
                    Confirm Reset
                  </button>

                  <button type="button" className="withdraw_sec_help" onClick={() => navigate("/user_profile/support")}>
                    How does this work?
                  </button>
                </div>
              </div>
            ) : null}

            {/* Step: Confirm */}
            {step === "confirm" ? (
              <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
                <div className="withdraw_sec_confirm_modal" role="dialog" aria-modal="true" aria-label="Confirm reset">
                  <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={() => setStep("select")}>
                    ×
                  </button>

                  <div className="withdraw_sec_confirm_hero" aria-hidden="true">
                    <img className="dark_img" src="/images/security/security_vector2.svg" alt="" />
                    <img className="light_img" src="/images/security/security_vector_light2.svg" alt="" />
                  </div>
                  <div className="withdraw_sec_confirm_title">Are You Sure You Want to Reset Your Security Methods?</div>

                  <label className="withdraw_sec_confirm_ack">
                    <input
                      className="withdraw_sec_confirm_ack_input"
                      type="checkbox"
                      checked={secResetAck}
                      onChange={(e) => setSecResetAck(e.target.checked)}
                    />
                    <span className={`withdraw_sec_confirm_ack_box withdraw_sec_confirm_ack_box--icon${secResetAck ? " is-checked" : ""}`} aria-hidden="true" />
                    <span>
                      In order to protect your account, withdrawals, P2P selling, and payment services may be disabled for 48 to 72 hours after
                      you make this change
                    </span>
                  </label>

                  <div className="withdraw_sec_confirm_actions">
                    <button type="button" className="withdraw_sec_btnGhost" onClick={() => setStep("select")}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="withdraw_sec_btnPrimary"
                      disabled={!secResetAck || startBusy}
                      onClick={() => {
                        void startUnavailableResetSession();
                      }}
                    >
                      {startBusy ? "Continuing…" : "Confirm"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Step: Requirements */}
            {step === "requirements" && !proofOverlayOpen ? (
              <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
                <div className="withdraw_sec_req_modal sec-req-invert-icons security-verification-requirements dark_theme_white_icons_popup" role="dialog" aria-modal="true" aria-label="Security Verification Requirements">
                  <div className="withdraw_sec_req_topbar">
                    <button type="button" className="withdraw_choose_back" aria-label="Back" onClick={() => setStep("confirm")}>
                      <i className="ri-arrow-left-long-line" aria-hidden="true" />
                    </button>
                    <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={() => navigate(-1)}>
                      ×
                    </button>
                  </div>

                  <div className="withdraw_sec_req_title">Security Verification Requirements</div>
                  <div className="withdraw_sec_req_subtitle">
                    Complete remaining enabled methods to continue.
                  </div>

                  <div className="withdraw_sec_req_progress">
                    {reqCompleted} / {reqTotal}
                  </div>

                  <div className="withdraw_sec_req_list">
                    {remainingVerify.passkeys ? (
                      <button
                        type="button"
                        className="withdraw_sec_req_item"
                        onClick={() => openProof("passkeys")}
                      >
                        <span className="withdraw_sec_req_left passkeyvector">
                          <img className="light_img" src="/images/security/passkey_light.svg" alt="" />
                          <img className="dark_img" src="/images/security/passkey.svg" alt="" />
                          <span>
                            <span className="withdraw_sec_req_name">Passkeys</span>
                            <span className="withdraw_sec_req_desc">Verify with biometrics or security keys</span>
                          </span>
                        </span>
                        <span className="withdraw_sec_req_right" aria-hidden="true">
                          {secReqDone.passkeys ? (
                            <i className="ri-check-line withdraw_sec_req_check" />
                          ) : (
                            <i className="ri-arrow-right-long-line"></i>
                          )}
                        </span>
                      </button>
                    ) : null}

                    {remainingVerify.authApp ? (
                      <button
                        type="button"
                        className="withdraw_sec_req_item"
                        onClick={() => openProof("authApp")}
                      >
                        <span className="withdraw_sec_req_left">
                          <span className="withdraw_sec_icon" aria-hidden="true">
                            <img src="/images/security/authenticator.svg" alt="" />
                          </span>
                          <span>
                            <span className="withdraw_sec_req_name">Authenticator App</span>
                          </span>
                        </span>
                        <span className="withdraw_sec_req_right" aria-hidden="true">
                          {secReqDone.authApp ? (
                            <i className="ri-check-line withdraw_sec_req_check" />
                          ) : (
                            <i className="ri-arrow-right-long-line"></i>
                          )}
                        </span>
                      </button>
                    ) : null}

                    {remainingVerify.phone ? (
                      <button
                        type="button"
                        className="withdraw_sec_req_item"
                        onClick={() => openProof("phone")}
                      >
                        <span className="withdraw_sec_req_left">
                          <span className="withdraw_sec_icon" aria-hidden="true">
                            <img src="/images/security/phone.svg" alt="" />
                          </span>
                          <span>
                            <span className="withdraw_sec_req_name">Phone Number</span>
                          </span>
                        </span>
                        <span className="withdraw_sec_req_right" aria-hidden="true">
                          {secReqDone.phone ? (
                            <i className="ri-check-line withdraw_sec_req_check" />
                          ) : (
                            <i className="ri-arrow-right-long-line"></i>
                          )}
                        </span>
                      </button>
                    ) : null}

                    {remainingVerify.email ? (
                      <button
                        type="button"
                        className="withdraw_sec_req_item"
                        onClick={() => openProof("email")}
                      >
                        <span className="withdraw_sec_req_left">
                          <span className="withdraw_sec_icon" aria-hidden="true">
                            <img src="/images/email_vector2.svg" alt="" />
                          </span>
                          <span>
                            <span className="withdraw_sec_req_name">Email</span>
                          </span>
                        </span>
                        <span className="withdraw_sec_req_right" aria-hidden="true">
                          {secReqDone.email ? (
                            <i className="ri-check-line withdraw_sec_req_check" />
                          ) : (
                            <i className="ri-arrow-right-long-line"></i>
                          )}
                        </span>
                      </button>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    className="withdraw_sec_primary"
                    disabled={!remainingProofDone || resetBusy}
                    onClick={async () => {
                      if (!remainingProofDone || resetBusy) return;
                      await submitUnavailableReset();
                    }}
                  >
                    {resetBusy ? "Submitting…" : "Continue"}
                  </button>

                  {remainingVerify.passkeys ? (
                    <button type="button" className="withdraw_sec_req_link" onClick={() => openProof("passkeys")}>
                      Verify with Passkey
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* Passkey */}
            {isPasskeyOpen ? (
              <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
                <div className="withdraw_passkey_modal" role="dialog" aria-modal="true" aria-label="Verify with passkey">
                  <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={() => setIsPasskeyOpen(false)}>
                    ×
                  </button>
                  <div className="withdraw_passkey_title">Verify with passkey</div>
                  <div className="withdraw_passkey_hero" aria-hidden="true">
                    <img className="light_img" src="/images/passkey_vector_light.svg" alt="" />
                    <img className="dark_img" src="/images/passkey_vector.svg" alt="" />
                  </div>
                  <div className="withdraw_passkey_text">Complete verification using your passkey</div>
                  <div className="withdraw_passkey_subtext">
                    Please follow the instructions on your browser to complete verification.
                  </div>
                  <button
                    type="button"
                    className="withdraw_passkey_verify"
                    disabled={passkeyBusy}
                    onClick={() => void verifyPasskeyProof()}
                  >
                    {passkeyBusy ? "Verifying…" : "Verify"}
                  </button>
                  <button
                    type="button"
                    className="withdraw_passkey_link"
                    onClick={markPasskeyUnavailable}
                  >
                    My Passkeys Are Not Available
                  </button>
                  <div className="withdraw_passkey_footer">
                    <img src="/images/protected_icon.svg" alt="shield icon" />
                    <span>Protected by Balance Risk</span>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Auth app */}
            {isAuthAppOpen ? (
              <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
                <div className="withdraw_auth_modal" role="dialog" aria-modal="true" aria-label="Authenticator App Verification">
                  <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={() => setIsAuthAppOpen(false)}>
                    ×
                  </button>
                  <div className="withdraw_auth_title">Authenticator App Verification</div>
                  <div className="withdraw_auth_subtitle">
                    Enter the 6-digit code generated by the
                    <br />
                    Authenticator App.
                  </div>
                  <div className="withdraw_auth_label">Authenticator App</div>
                  <div className="withdraw_auth_input_row">
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder=""
                      value={authAppCode}
                      onChange={(e) => setAuthAppCode(e.target.value)}
                    />
                    <button type="button" className="withdraw_auth_paste">
                      Paste
                    </button>
                  </div>
                  <button
                    type="button"
                    className="withdraw_auth_submit"
                    onClick={() => void verifyTotpCode()}
                  >
                    Submit
                  </button>
                  <div className="withdraw_passkey_footer withdraw_auth_footer">
                    <img src="/images/protected_icon.svg" alt="shield icon" />
                    <span>Protected by Balance Risk</span>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Email */}
            {isEmailVerifyOpen ? (
              <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
                <div className="withdraw_email_modal" role="dialog" aria-modal="true" aria-label="Email Verification">
                  <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={() => setIsEmailVerifyOpen(false)}>
                    ×
                  </button>
                  <div className="withdraw_email_title">Email Verification</div>
                  <div className="withdraw_email_subtitle">
                    Enter the 6-digit code Verification code sent to
                    <br />
                    {accountEmail ? maskedEmail : "your email"}
                  </div>
                  <div className="withdraw_email_label">Email Verification Code</div>
                  <div className="withdraw_email_input_row">
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder=""
                      value={emailOtp}
                      onChange={(e) => setEmailOtp(String(e.target.value || "").replace(/\D/g, "").slice(0, 6))}
                    />
                    <button
                      type="button"
                      className="withdraw_email_status"
                      disabled={otpSending}
                      onClick={() => void sendChannelOtp("email")}
                    >
                      {otpSending ? "Sending…" : "Get Code"}
                    </button>
                  </div>
                  <button
                    type="button"
                    className="withdraw_email_submit"
                    disabled={emailOtp.length !== 6}
                    onClick={() => void verifyChannelOtp("email")}
                  >
                    Submit
                  </button>
                  <div className="withdraw_passkey_footer withdraw_auth_footer">
                    <img src="/images/protected_icon.svg" alt="shield icon" />
                    <span>Protected by Balance Risk</span>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Mobile */}
            {isMobileVerifyOpen ? (
              <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
                <div className="withdraw_email_modal" role="dialog" aria-modal="true" aria-label="Mobile Verification">
                  <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={() => setIsMobileVerifyOpen(false)}>
                    ×
                  </button>
                  <div className="withdraw_email_title">Security Verification Requirements</div>
                  <div className="withdraw_email_subtitle">
                    Enter the 6-digit verification code sent to
                    <br />
                    {accountPhoneDigits ? maskedPhone : "your phone"}.
                  </div>
                  <div className="withdraw_email_label">Phone Verification Code</div>
                  <div className="withdraw_email_input_row">
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder=""
                      value={mobileOtp}
                      onChange={(e) => setMobileOtp(String(e.target.value || "").replace(/\D/g, "").slice(0, 6))}
                    />
                    <button
                      type="button"
                      className="withdraw_email_status"
                      disabled={otpSending}
                      onClick={() => void sendChannelOtp("mobile")}
                    >
                      {otpSending ? "Sending…" : "Get Code"}
                    </button>
                  </div>
                  <button
                    type="button"
                    className="withdraw_email_submit"
                    disabled={mobileOtp.length !== 6}
                    onClick={() => void verifyChannelOtp("mobile")}
                  >
                    Submit
                  </button>
                  <div className="withdraw_passkey_footer withdraw_auth_footer">
                    <img src="/images/protected_icon.svg" alt="shield icon" />
                    <span>Protected by Balance Risk</span>
                  </div>
                </div>
              </div>
            ) : null}

            {isRequestSubmittedOpen ? (
              <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
                <div className="withdraw_facial_modal withdraw_facial_result" role="dialog" aria-modal="true" aria-label="Request Submitted">
                  <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={closeAll}>
                    ×
                  </button>
                  <div className="withdraw_facial_submitHero"><img src="/images/security/submitted_vector.svg" alt="" /></div>
                  <div className="withdraw_facial_submitTitle">Request Submitted</div>
                  <div className="withdraw_facial_subtitle">
                    Your request has been successfully submitted. Please wait while we process it.
                  </div>
                  <button
                    type="button"
                    className="withdraw_sec_primary"
                    disabled={resetBusy}
                    onClick={async () => {
                      if (resetBusy) return;
                      const ok = await submitUnavailableReset();
                      if (!ok) return;
                      setIsRequestSubmittedOpen(false);
                      setIsSecurityResetSuccessOpen(true);
                    }}
                  >
                    OK
                  </button>
                </div>
              </div>
            ) : null}

            {step === "success" ? (
              <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
                <div className="withdraw_facial_modal withdraw_facial_result" role="dialog" aria-modal="true" aria-label="Security reset success">
                  <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={goSecurityHome}>
                    ×
                  </button>
                  <div className="withdraw_facial_successHero">
                    <img src="/images/sucessfull_payment_icon.svg" alt="" />
                  </div>
                  <div className="withdraw_facial_submitTitle mt-0">Security Methods Reset Successfully</div>
                  <div className="withdraw_facial_subtitle">
                    The methods you marked unavailable were disabled. Authenticator and other remaining methods stay on for login.
                    Withdrawals and P2P are frozen for 72 hours.
                  </div>
                  <button type="button" className="withdraw_sec_primary" onClick={goSecurityHome}>
                    OK
                  </button>
                </div>
              </div>
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
}

